import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { createProjectsApi } from "@/lib/api/projects";

interface DeleteProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectName: string;
  getToken: () => Promise<string | null>;
  /** If true, navigates to /dashboard/projects after deletion */
  navigateOnDelete?: boolean;
  onSuccess?: () => void;
}

export function DeleteProjectDialog({
  open,
  onOpenChange,
  projectId,
  projectName,
  getToken,
  navigateOnDelete = false,
  onSuccess,
}: DeleteProjectDialogProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirmInput, setConfirmInput] = useState("");
  const expected = `Delete ${projectName}`;
  const isConfirmed = confirmInput === expected;

  useEffect(() => {
    if (open) {
      setConfirmInput("");
    }
  }, [open]);

  const projectsApi = createProjectsApi({ getToken });

  const mutation = useMutation({
    mutationFn: () => projectsApi.delete(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.removeQueries({ queryKey: ["project", projectId] });
      onOpenChange(false);
      onSuccess?.();
      if (navigateOnDelete) navigate("/dashboard/projects");
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-surface-raised border-white/10 p-6">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-surface-raised-text">
            Delete project
          </DialogTitle>
        </DialogHeader>

        <div className="py-2 text-sm text-surface-raised-text-muted leading-relaxed">
          This action cannot be undone. This will{" "}
          <span className="text-red-400 font-medium">permanently delete</span>{" "}
          the project{" "}
          <span className="font-semibold text-surface-raised-text">
            {projectName}
          </span>{" "}
          and all of its data.
        </div>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <label
              htmlFor="delete-confirm"
              className="text-sm font-medium text-surface-raised-text"
            >
              Type{" "}
              <span className="font-mono bg-white/10 px-1.5 py-0.5 rounded text-xs text-surface-raised-text">
                Delete {projectName}
              </span>{" "}
              to confirm
            </label>
            <Input
              id="delete-confirm"
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              onKeyDown={(e) =>
                e.key === "Enter" && isConfirmed && mutation.mutate()
              }
              placeholder={expected}
              className="bg-surface-raised-hover border-white/10 text-surface-raised-text"
            />
          </div>
        </div>

        <DialogFooter className="bg-transparent border-t-0 -mx-6 -mb-6 p-6 pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
            className="bg-transparent border-white/20 text-surface-raised-text hover:bg-surface-raised-hover"
          >
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!isConfirmed || mutation.isPending}
            className="bg-red-600 text-white hover:bg-red-700"
          >
            {mutation.isPending ? "Deleting…" : "Delete project"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
